import { useCreateBillingPortal, usePayments, type Paiement } from '@fidalia/api';
import {
  Alert,
  Badge,
  Button,
  CardBrand,
  DataTable,
  PageHeader,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  MdCalendarToday,
  MdCreditCard,
  MdEuro,
  MdEventBusy,
  MdEventRepeat,
  MdOpenInNew,
  MdReceiptLong,
  MdWorkspacePremium,
} from 'react-icons/md';

import { errorMessage } from '@/lib/auth';

export const Route = createFileRoute('/_app/abonnement/paiements')({
  component: PaiementsPage,
});

const STATUT_LABELS: Record<
  Paiement['statut'],
  { tone: 'success' | 'brand' | 'danger'; label: string }
> = {
  paye: { tone: 'success', label: 'Payé' },
  offert: { tone: 'brand', label: 'Offert' },
  echec: { tone: 'danger', label: 'Échec' },
};

const NOM_FORMULE = { solo: 'Solo', cabinet: 'Cabinet', centre: 'Centre' } as const;
const NOM_PERIODE = { mensuel: 'Mensuel', annuel: 'Annuel' } as const;

const columns: TableColumn<Paiement>[] = [
  {
    key: 'date',
    title: 'Date',
    icon: MdCalendarToday,
    cell: (paiement) =>
      new Date(paiement.date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }),
  },
  {
    key: 'formule',
    title: 'Formule',
    icon: MdWorkspacePremium,
    cell: (paiement) =>
      paiement.formule ? (
        NOM_FORMULE[paiement.formule]
      ) : (
        <span className="text-content-subtle">—</span>
      ),
  },
  {
    key: 'expireLe',
    title: 'Expiration',
    icon: MdEventBusy,
    // C'est l'échéance telle qu'elle était à cette facture, pas l'échéance
    // actuelle du cabinet : après un changement de formule, les lignes plus
    // anciennes gardent une date dépassée depuis longtemps, et c'est correct.
    cell: (paiement) =>
      paiement.expireLe ? (
        new Date(paiement.expireLe).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })
      ) : (
        <span className="text-content-subtle">—</span>
      ),
  },
  {
    key: 'periode',
    title: 'Facturation',
    icon: MdEventRepeat,
    cell: (paiement) =>
      paiement.periode ? (
        NOM_PERIODE[paiement.periode]
      ) : (
        <span className="text-content-subtle">—</span>
      ),
  },
  {
    key: 'montant',
    title: 'Montant',
    icon: MdEuro,
    align: 'right',
    cell: (paiement) =>
      paiement.montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }),
  },
  {
    key: 'carte',
    title: 'Moyen de paiement',
    icon: MdCreditCard,
    // Rien n'a été prélevé sur une facture offerte : y afficher une carte
    // laisserait croire à un débit.
    cell: (paiement) =>
      paiement.carte && paiement.statut !== 'offert' ? (
        <span className="inline-flex items-center gap-2 text-content-muted">
          <CardBrand marque={paiement.carte.marque} />
          •••• {paiement.carte.derniers4}
        </span>
      ) : (
        <span className="text-content-subtle">—</span>
      ),
  },
  {
    key: 'statut',
    title: 'Statut',
    cell: (paiement) => (
      <Badge tone={STATUT_LABELS[paiement.statut].tone}>
        {STATUT_LABELS[paiement.statut].label}
      </Badge>
    ),
  },
  {
    key: 'facture',
    align: 'right',
    className: 'w-px',
    cell: (paiement) =>
      paiement.factureUrl ? (
        <a
          href={paiement.factureUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-content-muted transition-colors hover:bg-surface-muted hover:text-content"
        >
          Facture
          <MdOpenInNew className="size-4" aria-hidden="true" />
        </a>
      ) : null,
  },
];

function PaiementsPage() {
  const navigate = useNavigate();
  const { data, isPending, error } = usePayments();
  const portail = useCreateBillingPortal();
  const [erreurPortail, setErreurPortail] = useState<string | null>(null);

  const ouvrirPortail = async () => {
    setErreurPortail(null);

    // Onglet ouvert seulement une fois l'URL en main : ouvrir une page vide
    // avant l'appel réseau affichait un onglet blanc qui chargeait plusieurs
    // secondes, sans rien à regarder. Le `loading` du bouton porte l'attente
    // à la place — un seul indicateur, au bon endroit.
    //
    // Revers assumé : `window.open` après un `await` n'est plus rattaché au
    // clic pour certains navigateurs, qui peuvent le bloquer. Le repli sur
    // l'onglet courant couvre ce cas.
    try {
      const { url } = await portail.mutateAsync();
      const onglet = window.open(url, '_blank');
      if (onglet) onglet.opener = null;
      else window.location.href = url;
    } catch (cause) {
      setErreurPortail(errorMessage(cause, "Impossible d'ouvrir la gestion du paiement."));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdReceiptLong />}
        title="Mes paiements"
        description="L'historique de vos factures et de vos règlements."
        back={{
          label: "Retour à l'abonnement",
          onClick: () => void navigate({ to: '/abonnement' }),
        }}
        actions={
          <Button
            variant="outline"
            icon={<MdCreditCard className="size-5" />}
            loading={portail.isPending}
            onClick={() => void ouvrirPortail()}
          >
            Gérer mon moyen de paiement
          </Button>
        }
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}
      {erreurPortail ? <Alert tone="danger">{erreurPortail}</Alert> : null}

      <DataTable
        data={data?.items ?? []}
        columns={columns}
        isLoading={isPending}
        rowId={(paiement) => paiement.id}
        emptyMessage="Aucun paiement pour l'instant."
      />
    </div>
  );
}
